<template>
    <div class="container-fluid">
        <div class="row">
            <div class="col-lg-10 col-sm-12 bg-white shadow-sm rounded-lg border mb-5">
                <form
                    class="m-3"
                    @submit.prevent="submit"
                >
                    <!-- title -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.title }} <span class="required-star"></span>
                        </label>
                        <div class="col-sm-9">
                            <input
                                type="text"
                                class="form-control"
                                maxlength="50"
                                v-model="title"
                            />
                            <span
                                class="text-danger"
                                v-if="errors && errors.title"
                            >{{ errors.title[0] }}</span>
                        </div>
                    </div>
                    <!-- image -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.image }} <span class="required-star"></span>
                        </label>
                        <div class="col-sm-9">
                            <label
                                for="imageFile"
                                role="button"
                            ><img
                                    :src="areaData ? areaData.image_url : '/img/no-image.jpg'"
                                    class="icon-size-100"
                                    id="preview-image"
                                ></label>
                            <input
                                type="file"
                                class="form-control-file d-none"
                                id="imageFile"
                                accept=".jpeg, .png, .svg"
                                @change="uploadImage"
                            />
                            <small class="form-text text-muted">
                                {{ trans.area_image_size }}
                            </small>
                            <p
                                class="text-danger"
                                v-if="errors && errors.image"
                            >{{ errors.image[0] }}</p>
                        </div>
                    </div>
                    <!-- area type -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.type }} <span class="required-star"></span>
                        </label>
                        <div class="col-sm-6">
                            <select
                                class="form-control"
                                v-model="type"
                                @change="upperAreaId = ''"
                            >
                                <option
                                    selected
                                    value=""
                                >Choose...</option>
                                <option
                                    :value="typeVal"
                                    v-for="typeTxt, typeVal in areaTypes"
                                    :key="typeVal"
                                >
                                    {{ typeTxt }}
                                </option>
                            </select>
                            <small class="form-text text-muted">
                                {{ trans.area_type }}
                            </small>
                            <span
                                class="text-danger"
                                v-if="errors && errors.type"
                            >{{ errors.type[0] }}</span>
                        </div>
                    </div>
                    <!-- upper area -->
                    <div class="form-group row" v-if="type != 3">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.upper_area }}
                        </label>
                        <div class="col-sm-6">
                            <select
                                class="form-control"
                                v-model="upperAreaId"
                            >
                                <option
                                    selected
                                    value=""
                                >Choose...</option>
                                <option
                                    :value="upperArea.id"
                                    v-for="upperArea in upperAreas"
                                    :key="'upper-area-' + upperArea.id"
                                    v-show="isShowUpperArea(upperArea)"
                                >
                                    {{ `[${upperArea.trans_type}] ` + upperArea.title }}
                                </option>
                            </select>
                            <span
                                class="text-danger"
                                v-if="errors && errors.upper_area_id"
                            >{{ errors.upper_area_id[0] }}</span>
                        </div>
                    </div>
                    <!-- zoom range -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.zoom_range }} <span class="required-star"></span>
                        </label>
                        <div class="col-sm-9">
                            <div class="row">
                                <div class="col-sm-4">
                                    <select
                                        class="form-control"
                                        v-model="fromRange"
                                    >
                                        <option
                                            v-for="i, iIndex in 22"
                                            :value="iIndex"
                                            :key="'fromRange-' + iIndex"
                                        >
                                            {{ iIndex }}
                                        </option>
                                    </select>
                                    <span
                                        class="text-danger"
                                        v-if="errors && errors.from_range"
                                    >{{ errors.from_range[0] }}</span>
                                </div>
                                <div class="col-sm-4">
                                    <select
                                        class="form-control"
                                        v-model="toRange"
                                    >
                                        <option
                                            v-for="j, jIndex in 22"
                                            :value="jIndex"
                                            :key="'toRange-' + jIndex"
                                            v-show="jIndex > fromRange"
                                        >
                                            {{ jIndex }}
                                        </option>
                                    </select>
                                    <span
                                        class="text-danger"
                                        v-if="errors && errors.to_range"
                                    >{{ errors.to_range[0] }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- click zoom -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.click_zoom }} <span class="required-star"></span>
                        </label>
                        <div class="col-sm-3">
                            <select
                                class="form-control"
                                v-model="clickZoom"
                            >
                                <option
                                    v-for="n, nIndex in 22"
                                    :value="nIndex"
                                    :key="'clickZoom-' + nIndex"
                                    v-show="nIndex > toRange"
                                >
                                    {{ nIndex }}
                                </option>
                            </select>
                            <span
                                class="text-danger"
                                v-if="errors && errors.click_zoom"
                            >{{ errors.click_zoom[0] }}</span>
                        </div>
                    </div>
                    <!-- address -->
                    <div class="form-group row">
                        <div
                            id="geocoder-container"
                            class="d-none"
                        ></div>
                        <label class="col-sm-3 col-form-label">
                            {{ trans.address }}
                        </label>
                        <div class="col-sm-9 d-flex">
                            <input
                                type="text"
                                class="form-control"
                                v-model="address"
                            />
                            <button
                                @click="$refs.mySmartMap.queryLocation(address)"
                                type="button"
                                class="btn btn-outline-primary mt-auto ml-1 w-25"
                            >
                                {{ trans.btn.search }}
                            </button>
                        </div>
                    </div>
                    <!-- coordinates -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.map_coordinates }}
                        </label>
                        <div class="col-sm-9 pl-3">
                            <div class="row d-flex">
                                <div class="">
                                    <span> {{ trans.latitude }} </span>
                                    <input
                                        type="number"
                                        min="-90"
                                        max="90"
                                        step="any"
                                        class="form-control"
                                        v-model="lat"
                                    />
                                </div>
                                <div class="ml-2">
                                    <span> {{ trans.longtitude }} </span>
                                    <input
                                        type="number"
                                        min="-180"
                                        max="180"
                                        step="any"
                                        class="form-control"
                                        v-model="lng"
                                    />
                                </div>
                                <button
                                    class="btn btn-outline-primary mt-auto ml-2"
                                    type="button"
                                    :disabled="!lngLatIsValid"
                                    @click="
                                        $refs.mySmartMap.setFlyToForMarker(
                                            coordinates
                                        );
                                    "
                                >
                                    {{ trans.btn.apply }}
                                </button>
                            </div>
                            <p class="text-danger">
                                <span v-if="
                                        !lngLatIsValid &&
                                        !errors.lng &&
                                        !errors.lat
                                    ">Longtitude must be between -180 and 180.
                                    Latitude must be between -90 and 90</span>
                                <span v-else-if="errors && errors.lng">{{
                                    errors.lng[0]
                                }}</span>
                                <span v-else-if="errors && errors.lat">{{
                                    errors.lat[0]
                                }}</span>
                            </p>
                            <div
                                class="row"
                                v-if="tokenIsValid"
                            >
                                <map-preview
                                    ref="mySmartMap"
                                    :access-token="mapData.mapbox_token"
                                    :zoom-level="mapData.zoom_level"
                                    :map-style="mapData.style"
                                    :has-marker="true"
                                    :draggable="true"
                                    :coordinates="coordinates"
                                    :marker-coordinates="coordinates"
                                    @updatedMarkerCoordinates="updateCoordinates"
                                ></map-preview>
                            </div>
                        </div>
                    </div>
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.status }}
                        </label>
                        <div class="col-sm-9">
                            <div class="custom-control custom-switch">
                                <input
                                    type="checkbox"
                                    class="custom-control-input"
                                    id="customSwitch1"
                                    v-model="status"
                                    true-value="1"
                                    false-value="0"
                                >
                                <label
                                    class="custom-control-label text-muted"
                                    for="customSwitch1"
                                >{{ trans.status_val[status] }}</label>
                            </div>
                        </div>
                    </div>
                </form>
                <button
                    class="btn btn-primary w-25 center mb-4 mt-4 float-right submit-btn"
                    :disabled="isDisabled"
                    >
                        {{ areaData ? trans.btn.update : trans.btn.create }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import MapPreview from "./MapPreview";
import CheckAccessToken from "../mixins/MapBox/CheckAccessToken";

export default {
    props: {
        trans: {
            type: Object,
            required: true,
        },
        areaTypes: {
            type: Object,
            required: true,
        },
        upperAreas: {
            type: Array,
            required: true,
        },
        mapData: {
            type: Object,
            required: true,
        },
        areaData: {
            type: Object,
            default: function () {
                return null;
            },
        },
        formAction: {
            type: String,
            required: true,
        },
        isEdit: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        MapPreview,
    },
    mixins: [CheckAccessToken],
    data() {
        return {
            title: "",
            image: null,
            type: "",
            upperAreaId: "",
            fromRange: 2,
            toRange: 10,
            clickZoom: 11,
            lng: 135,
            lat: 39,
            address: "",
            status: 0,
            errors: {},
            isDisabled: false,
        };
    },
    computed: {
        lngLatIsValid() {
            return Math.abs(this.lng) <= 180 && Math.abs(this.lat) <= 90;
        },
        coordinates() {
            return [this.lng, this.lat];
        },
    },
    methods: {
        updateCoordinates(payload) {
            [this.lng, this.lat] = payload;
        },
        prepareFormData() {
            let formData = new FormData();
            formData.append("status", this.status);
            formData.append("title", this.title);
            if (this.image instanceof File) {
                formData.append("image", this.image);
            }
            formData.append("type", this.type);
            if (this.upperAreaId) {
                formData.append("upper_area_id", this.upperAreaId);
            }
            formData.append("from_range", this.fromRange);
            formData.append("to_range", this.toRange);
            formData.append("click_zoom", this.clickZoom);
            formData.append("lat", this.lat);
            formData.append("lng", this.lng);
            if (this.isEdit) {
                formData.append("_method", "PUT");
            }

            return formData;
        },
        submit() {
            this.isDisabled = true;
            var self = this;
            axios({
                method: "POST",
                url: this.formAction,
                data: this.prepareFormData(),
                headers: {
                    "content-type": "multipart/form-data",
                },
            })
                .then((response) => {
                    if (response.data.redirectTo != undefined) {
                        setTimeout(() => {
                            window.location.replace(response.data.redirectTo);
                        }, 2000);
                    }
                })
                .catch((error) => {
                    self.isDisabled = false;
                    if (error.response.status == 422) {
                        this.errors = {};
                        this.errors = error.response.data.errors;
                    }
                });
        },
        fetch() {
            this.title = this.areaData.title;
            this.type = this.areaData.type;
            this.upperAreaId = this.areaData.upper_area_id;
            [this.lng, this.lat] = this.areaData.coordinates;
            [this.fromRange, this.toRange] = this.areaData.zoom_range;
            this.clickZoom = this.areaData.click_zoom;
            this.status = this.areaData.status ? 1 : 0;
        },
        uploadImage(event) {
            if (event.target.files.length == 0) return;
            const file = event.target.files;
            if (file) {
                let previewImage = document.getElementById("preview-image");
                previewImage.src = URL.createObjectURL(file[0]);
                this.image = file[0];
            }
        },
        isShowUpperArea(upperArea) {
            return upperArea.type == parseInt(this.type) + 1 && (!this.isEdit || this.areaData.id != upperArea.id)
        },
        clickSubmitBtn() {
            var self = this;
            let submitBtns = document.getElementsByClassName('submit-btn');
            if (submitBtns.length > 0) {
                for (let i = 0; i < submitBtns.length; i++) {
                    const btn = submitBtns[i];
                    btn.addEventListener('click', function () {
                        self.submit();
                        this.disabled = true;
                        let formLanguages = document.querySelectorAll(".tab-content form");
                        formLanguages.forEach(function (form) {
                            var formData = new FormData(form);
                            axios.post(form.action, formData, {
                                headers: {
                                    "Content-Type": "multipart/form-data",
                                },
                            })
                        });
                    });
                }
            }
        },
    },
    created() {
        if (this.isEdit) {
            this.fetch();
        }
    },
    mounted() {
        this.clickSubmitBtn();
        this.checkAccessToken(this.mapData.mapbox_token);
    }
};
</script>

<style>
</style>